<script setup lang="ts">
  import { computed, ref, useAttrs, useSlots, watch } from 'vue';

  import { money, percent } from '../../utils/i18n';
  import Input from '../Input/Input.vue';

  defineOptions({
    name: 'll-inline-edit',
    inheritAttrs: false,
  });

  export interface InlineEditProps {
    /**
     * Formats the value displayed.
     * Options: currency, percent
     */
    format?: 'currency' | 'percent' | '';

    /**
     * DOM `type` property to be passed to `ll-input`.
     * Use type="number" for localized numbers
     */
    type?: string;

    /**
     * The current value for the component.
     */
    modelValue?: string | number;

    /**
     * Used to display a crossed out value below the component
     * if the current `modelValue` does not match `oldValue`.
     */
    oldValue?: string | number;

    /**
     * Label to render for the input
     */
    label?: string;

    /**
     * Placeholder for the input
     */
    placeholder?: string;

    /**
     * Error text to display. Replaces `hintText` (if provided) & adds error styling.
     */
    errorText?: string;

    /**
     * Hint text to display below the input
     */
    hintText?: string;
  }

  const slots = useSlots();
  const attrs = useAttrs();

  const props = withDefaults(defineProps<InlineEditProps>(), {
    format: '',
    type: 'text',
    modelValue: undefined,
    oldValue: undefined,
    label: ' ',
    placeholder: '',
    errorText: '',
    hintText: '',
  });

  const emit = defineEmits<{
    /**
     * Emitted when the value is updated
     */
    (e: 'update:model-value', value: string | number | undefined): void;
  }>();

  const editing = ref(false);

  // this value gets set once and never changes
  const originalValue = ref<InlineEditProps['oldValue']>(props.oldValue || props.modelValue);

  // the _current_ value. This is a temporary value, used while editing
  const internalValue = ref<InlineEditProps['modelValue']>(props.modelValue);

  const inputAttrs = computed(() => {
    const tempAttrs = { ...attrs };

    delete tempAttrs.class;
    return tempAttrs;
  });

  const formattedOriginalValue = computed(() => formatValue(originalValue.value || ''));

  const showOldValue = computed(() => {
    return (props.oldValue || props.oldValue === 0) && props.oldValue !== internalValue.value;
  });

  const isAppended = computed(() => {
    return !!slots.append;
  });

  const isPrepended = computed(() => {
    return !!slots.prepend;
  });

  watch(
    () => props.modelValue,
    (newVal?: string | number, oldVal?: string | number) => {
      if (newVal !== oldVal) {
        internalValue.value = newVal;
      }
    },
  );

  function dismiss() {
    internalValue.value = props.modelValue || props.oldValue;
    editing.value = false;
    blurInput();
  }

  /**
   * Toggles edit state on
   */
  function edit() {
    editing.value = true;
    focusInput();
  }

  /**
   * Formats the value based on `format`
   */
  function formatValue(value: string | number) {
    switch (props.format) {
      case 'currency':
        return money({
          amount: value as number,
        });
      case 'percent':
        return percent((value as number) / 100);
      default:
        return value;
    }
  }

  /**
   * This method saves the current value of the input field by `$emit`ing.
   * If the new value is empty, it reverts `internalValue` to the original value.
   */
  function save() {
    internalValue.value = !(internalValue.value === '') ? internalValue.value : originalValue.value;

    emit('update:model-value', internalValue.value);
    editing.value = false;
    blurInput();
  }

  const llInput = ref();

  function focusInput() {
    if (llInput.value) {
      llInput.value.inputEl.focus();
    }
  }

  function blurInput() {
    if (llInput.value) {
      llInput.value.inputEl.blur();
    }
  }
</script>

<template>
  <div class="stash-inline-edit" :class="attrs.class" data-test="stash-inline-edit">
    <div class="stash-inline-edit__input-wrapper">
      <Input
        ref="llInput"
        v-model="internalValue"
        :error-text="props.errorText"
        :hint-text="props.hintText"
        :label="props.label"
        :placeholder="`${props.placeholder || originalValue}`"
        :type="props.type"
        v-bind="inputAttrs"
        @blur="save"
        @focus="edit"
        @keydown.esc="dismiss"
        @keyup.enter="blurInput"
      >
        <template v-if="isPrepended" #prepend>
          <!-- @slot renders content on the left side of the input -->
          <slot name="prepend"> </slot>
        </template>

        <template v-if="isAppended" #append>
          <!-- @slot renders content on the right side of the input -->
          <slot name="append"> </slot>
        </template>
      </Input>
    </div>

    <span v-if="showOldValue" class="mt-1 inline-block pl-3 text-ice-700 line-through">
      {{ formattedOriginalValue }}
    </span>
  </div>
</template>
